import React, { useMemo, useState } from 'react';
import { Clock } from 'lucide-react';
import PillToggle from '../UI/PillToggle';
import EmploymentHistorySection from './EmploymentHistorySection';
import {
  useEmployeeHRLetters,
  useEmployeeCertificates,
  useEmployeeTrainingEnrollments,
  useEmployeeEducation,
} from '../../hooks/useTimelineData';
import { formatDate } from '../../utils/dateUtils';
import {
  buildCareerTimelineEvents,
  CAREER_TIMELINE_EVENT_STYLES,
} from '../../utils/careerTimelineUtils';
import CareerTimelineEventBody from './CareerTimelineEventBody';

/**
 * Admin Employment tab: Career Timeline & History with Timeline / Table toggle.
 * Timeline = join / probation / letters / certs / movements narrative.
 * Table = Record Employment Event movement audit (flat EmploymentHistorySection).
 */
export default function CareerTimelineHistorySection({
  employee,
  movements = [],
  loading = false,
  theme = 'admin',
}) {
  const [view, setView] = useState('timeline');
  const staffId = employee?.staff_id;

  const { data: hrLetters = [] } = useEmployeeHRLetters(staffId);
  const { data: certificates = [] } = useEmployeeCertificates(staffId);
  const { data: trainingEnrollments = [] } = useEmployeeTrainingEnrollments(staffId);
  const { data: educationRecords = [] } = useEmployeeEducation(staffId);

  const events = useMemo(
    () => buildCareerTimelineEvents({
      employee,
      hrLetters,
      certificates,
      trainingEnrollments,
      educationRecords,
      employmentMovements: movements,
      audience: theme === 'employee' ? 'employee' : 'admin',
    }),
    [employee, hrLetters, certificates, trainingEnrollments, educationRecords, movements, theme],
  );

  const iconColor = theme === 'employee' ? 'text-blue-800' : 'text-red-600';

  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
        <h3 className="text-card-header flex items-center gap-2 min-w-0">
          <Clock className={`w-4 h-4 ${iconColor} shrink-0`} aria-hidden="true" />
          Career Timeline & History
        </h3>
        <PillToggle
          value={view}
          onChange={setView}
          options={[
            { id: 'timeline', label: 'Timeline' },
            { id: 'table', label: 'Table' },
          ]}
          variant="text"
          theme={theme}
          layout="sectionHeader"
          ariaLabel="Career history view"
        />
      </div>
      <p className="text-xs text-gray-500 mb-4">
        {view === 'timeline'
          ? 'Join date, probation, org events, letters, and credentials during employment.'
          : 'Org events recorded via Record Employment Event (promotions, transfers, RO / status / salary).'}
      </p>

      {view === 'timeline' ? (
        <div className="bg-white rounded-xl border p-4">
          {events.length === 0 ? (
            <div className="text-center py-10 text-gray-600">No career events yet.</div>
          ) : (
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" />
              <ul className="space-y-6">
                {events.map((e, idx) => {
                  const style = CAREER_TIMELINE_EVENT_STYLES[e.type] || CAREER_TIMELINE_EVENT_STYLES.system;
                  return (
                    <li key={`${e.type}-${e.date}-${idx}`} className="relative pl-12">
                      <div className={`absolute left-2 w-4 h-4 rounded-full ${style.dot} border-2 border-white shadow`} />
                      <div>
                        <div className="text-xs text-gray-500">
                          {e.yearOnly ? new Date(e.date).getFullYear() : formatDate(e.date)}
                        </div>
                        <CareerTimelineEventBody
                          title={e.title}
                          subtitle={e.subtitle}
                          details={e.details}
                          subtitleClassName={style.label}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <EmploymentHistorySection
          staffId={staffId}
          theme={theme}
          detailLevel="full"
          variant="flat"
          movements={movements}
          loading={loading}
        />
      )}
    </div>
  );
}
